import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Checkbox,
  Button,
  Row,
  Col,
  Divider,
  message,
} from "antd";
import dayjs from "dayjs";
import AxiosInstance from "../../AxiosInstance";
import "./LoanForm.css"; // ✅ IMPORTANT: use LoanForm CSS
import { useNavigate } from "react-router-dom";
import { getIpAddress } from "../../utils/getIpAddress";

const { Option } = Select;

// Static consents for PAN validation
const STATIC_CONSENTS = [
  {
    key: "privacy_terms",
    isMandatory: true,
    matchText: "Privacy policy and Product terms",
    message: "I have read, understood and hereby accept Privacy policy and Product terms and conditions of Bajaj Finance Limited.",
  },
  {
    key: "promotional_communications",
    isMandatory: true,
    matchText: "promotional communication",
    message: "I hereby expressly authorize Bajaj Finance Limited and their representative to send promotional communication about all products/services rendered by Bajaj Finance limited through phone calls/SMSs/emails/WhatsApp or any other electronic mode",
  },
  {
    key: "data_sharing",
    isMandatory: false,
    matchText: "share my personal data",
    message: "I authorize to share my personal data with Bajaj Finance limited to process my loan application.",
  },
  {
    key: "pan_verification",
    isMandatory: true,
    matchText: "consent for PAN verification",
    message: "I hereby provide my consent for PAN verification and understand that any consent for sharing my details/data with Bajaj Finance Limited (BFL) shall be obtained separately at the applicable stage.",
  },
  {
    key: "tu_cibil",
    isMandatory: true,
    matchText: "consent for TU & CIBIL",
    message: (
          <>
            I have read, understood and hereby accept the{" "}
            <a
              href="https://www.bajajfinserv.in/privacy-policy"
              target="_blank"
              rel="noreferrer"
              style={{ color: "blue", textDecoration: "underline" }}
            >
              Privacy Policy & Product Terms and Conditions 
            </a>{" "}
            of Bajaj Finance Limited (“BFL”). I authorize BFL to receive my credit information from TU CIBIL and agree to the{" "}
            <a
              href="https://www.bajajfinserv.in/tnc-cibil"
              target="_blank"
              rel="noreferrer"
              style={{ color: "blue", textDecoration: "underline" }}
            >
             Terms and Conditions{" "}
            </a>
            of TU CIBIL. 
          </>
        ),
  }
  // {
  //   key: "credit_report_authorization",
  //   isMandatory: true,
  //   matchText: "fetch my mutual fund, credit report",
  //   message: "I authorize (CAMS & KFIN or MFC) to fetch my mutual fund, credit report and share data with Bajaj Finance Limited.",
  // },
];

// Fetch from API only to resolve consentIds at submit time
const getPanConsentsFromApi = async () => {
  try {
    const response = await AxiosInstance().get("/loan-consent/filter", {
      params: { screenName: "PAN", isActive: true },
    });
    if (response.data?.success) return response.data.data;
    return [];
  } catch (error) {
    return [];
  }
};

// Fetch user's existing consent statuses by mobile
const getUserConsentsByMobile = async (mobileNumber) => {
  try {
    const response = await AxiosInstance().get("/loan-consent/consents-by-mobile", {
      params: { mobileNumber },
    });
    if (response.data?.success) return response.data.data.consents;
    return [];
  } catch (error) {
    return [];
  }
};

export default function PanValidation({
  loanId,
  loanStepId,
  stepId,
  embedded = true,
  onSuccess,
}) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConsentMsg, setShowConsentMsg] = useState(false);
  // consentStates keyed by STATIC_CONSENTS key
  const [consentStates, setConsentStates] = useState({});
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      panNumber: "",
      fullName: "",
      dob: null,
      // fatherName: "",
      // motherName: "",
      // maritalStatus: "",
      authorization: false,
    },
  });

  /* ================= LOAD USER ================= */
  useEffect(() => {
    if (!loanId) return;
    fetchUser();
  }, [loanId]);

  const fetchUser = async () => {
    try {
      const resp = await AxiosInstance().get("/user/me");
      const data = resp.data;

      setUserData(data);
      setValue("panNumber", data.pan || "");
      setValue("fullName", `${data.firstName || ""} ${data.lastName || ""}`.trim());
      setValue("dob", data.dob ? dayjs(data.dob) : null);
      setValue("fatherName", data.relativeName || "");
      setValue("maritalStatus", data.maritalStatus || "");
      
      // Prefill consent states from user's existing consents if mobile is available
      if (data.phoneNumber && data.phoneNumber.length === 10) {
        const userConsents = await getUserConsentsByMobile(data.phoneNumber);
        // Map API consentId back to static key via matchText
        const apiConsents = await getPanConsentsFromApi();
        const newStates = {};
        STATIC_CONSENTS.forEach((staticConsent) => {
          const apiMatch = apiConsents.find((c) =>
            c.consentMessage?.includes(staticConsent.matchText) && c.screenName === "PAN"
          );
          if (apiMatch) {
            const userMatch = userConsents.find((uc) => 
              uc.consentId === apiMatch.consentId && 
              (uc.loanId === parseInt(loanId) || uc.loanId === null)
            );
            if (userMatch) newStates[staticConsent.key] = userMatch.consentStatus;
          }
        });
        setConsentStates(newStates);
      }
    } catch {
      message.error("Failed to load PAN details");
    }
  };

  // Fetch API consents to get IDs, match with static consents, then submit/update
  const submitConsentData = async (userData) => {
    try {
      // Validate user data
      if (!userData || !userData.userId) {
        message.error("User data is incomplete. Please try again.");
        return;
      }
      const ipAddress = await getIpAddress();
      const apiConsents = await getPanConsentsFromApi();
      
      // Get existing user consents to check if we need to update or create
      const existingUserConsents = await getUserConsentsByMobile(userData.phoneNumber);
      
      const consentTransactions = STATIC_CONSENTS.map((staticConsent) => {
        const apiMatch = apiConsents.find((c) =>
          c.consentMessage?.includes(staticConsent.matchText) && c.screenName === "PAN"
        );
        
        if (!apiMatch) return null;
        
        // Check if this consent already exists for the user (either loan-specific or global)
        const existingConsent = existingUserConsents.find(uc => 
          uc.consentId === apiMatch.consentId && 
          (uc.loanId === parseInt(loanId) || uc.loanId === null)
        );
        
        const consentData = {
          consentId: apiMatch.consentId,
          consentStatus: consentStates[staticConsent.key] || false,
          isActive: true,
          userId: userData.userId,
          loanId: parseInt(loanId),
          mobileNumber: userData.phoneNumber,
          ipAddress:ipAddress
        };
        
        return {
          ...consentData,
          existingTransaction: existingConsent, // Will be used to determine create vs update
        };
      }).filter((t) => t && t.consentId !== null); // skip unmatched
      
      for (const consentTx of consentTransactions) {
        try {
          if (consentTx.existingTransaction) {
            // Update existing transaction using PUT endpoint
            const updateData = {
              consentStatus: consentTx.consentStatus,
              ipAddress: ipAddress,
            };
            await AxiosInstance().put(`/loan-consent/transaction/${consentTx.existingTransaction.loanConsentId}`, updateData);
          } else {
            // Create new transaction using POST endpoint
            const createData = {
              ...consentTx,
              createdBy: String(userData.userId),
              UpdatedBy: null,
              ipAddress:ipAddress
            };
            delete createData.existingTransaction; // Remove helper property
            await AxiosInstance().post("/loan-consent/transaction", createData);
          }
        } catch (error) {
          // Error handling without console logging
        }
      }
    } catch (error) {
      // Error handling without console logging
    }
  };

  const handleConsentChange = (key, checked) => {
    setConsentStates((prev) => ({ ...prev, [key]: checked }));
  };

  const areMandatoryConsentsAccepted = () =>
    STATIC_CONSENTS.filter((c) => c.isMandatory).every((c) => consentStates[c.key] === true);

  /* ================= SUBMIT ================= */
  const onSubmit = async () => {
    if (!loanId || !userData) {
      return;
    }

    // Check if all mandatory consents are accepted
    if (!areMandatoryConsentsAccepted()) {
      message.error("Please accept all mandatory consents");
      setShowConsentMsg(true);
      setTimeout(() => setShowConsentMsg(false), 3000);
      return;
    }

    try {
      setLoading(true);

      // Submit consent data first
      await submitConsentData(userData);

      const payload = {
        loanId,
        stepId,
        loanStepId,
        userId: userData.userId,
        data: {
          consentAt: new Date().toISOString(),
        },
      };

      const resp = await AxiosInstance().post("/policy/loan/step", payload);

      if (resp?.data?.msg === "Successfully Completed") {
        message.success("PAN verification completed");
        embedded
          ? window.location.reload()
          : navigate(`/phdashboard/loan/${loanId}`);
      } else {
        message.error("PAN verification completed with unexpected response");
      }
    } catch (error) {
      message.error("PAN verification failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI (MATCHES LOAN FORM) ================= */

  return (
    <Form
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <div className="form-section">
        <div className="step-header text-center">
        <h2>PAN Verification</h2>
         {/* <p>Complete the highlighted step to proceed</p> */}
      </div>

        <h3 className="section-title">PAN Verification</h3>

        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Form.Item label="PAN Number" >
              <Controller
                name="panNumber"
                control={control}
                render={({ field }) => (
                  <Input {...field} readOnly style={{ textTransform: "uppercase" }} />
                )}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Name as per PAN" >
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => <Input {...field} readOnly />}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Date of Birth">
              <Controller
                name="dob"
                control={control}
                render={({ field }) => (
                  <DatePicker {...field} disabled style={{ width: "100%" }} />
                )}
              />
            </Form.Item>
          </Col>

          

          

          
        </Row>
         {/* PAN Consents */}
      <div className="mb-3">
        {STATIC_CONSENTS.map((consent) => (
          <div key={consent.key} style={{ marginBottom: "10px" }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", margin: 0 }}>
              <input
                type="checkbox"
                checked={consentStates[consent.key] || false}
                onChange={(e) => handleConsentChange(consent.key, e.target.checked)}
                style={{ marginTop: "3px", flexShrink: 0, width: "14px", height: "14px", cursor: "pointer" }}
              />
              <span style={{ fontSize: "13px", lineHeight: "1.4", textAlign: "left" }}>
                {consent.message}
                {consent.isMandatory && (
                  <span style={{ color: "red", marginLeft: "4px" }}>* (Required)</span>
                )}
              </span>
            </label>
          </div>
        ))}
      </div>
      
      {showConsentMsg && (
        <div style={{ marginTop: "10px", fontSize: "14px", color: "red", textAlign: "center" }}>
          Please agree to the consent terms to proceed.
        </div>
      )}

      

      <div className="text-center mt-4">
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={loading}
          className="submit-button"
          onClick={(e) => {
            if (!areMandatoryConsentsAccepted()) {
              e.preventDefault();
              setShowConsentMsg(true);
              setTimeout(() => setShowConsentMsg(false), 3000);
            }
          }}
        >
          Submit
        </Button>
      </div>
      </div>

      <Divider />

     
    </Form>
  );
}
